import { NfcTagRequestStatus } from "@clipboard-health/contract-backend-main";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { capitalizeFirstLetter } from "@src/appV2/lib/utils/capitalizeFirstLetter";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { Text } from "@src/lib/ui-react";
import { useState } from "react";

import { type NfcTag, type SaveNfcTagLocationParams } from "../types";

interface NfcTagRowProps {
  tag: NfcTag;
  canDelete: boolean;
  deleteDisabledReason?: string;
  onSaveLocation?: (params: SaveNfcTagLocationParams) => Promise<void>;
  onDeleteTag?: (tagId: string) => Promise<void>;
}

function formatStatus(status: NfcTag["tagStatus"]): string {
  return capitalizeFirstLetter(status.replaceAll("_", " ").toLowerCase());
}

export function NfcTagRow(props: NfcTagRowProps) {
  const { tag, canDelete, deleteDisabledReason, onSaveLocation, onDeleteTag } = props;
  const isMobile = useIsMobile();
  const [localLocation, setLocalLocation] = useState(tag.tagLocation);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const hasChanges = localLocation !== tag.tagLocation;
  const isTagRequested = tag.tagStatus === NfcTagRequestStatus.REQUESTED;
  const isLocationRequired =
    tag.tagStatus === NfcTagRequestStatus.ON_ITS_WAY ||
    tag.tagStatus === NfcTagRequestStatus.DELIVERED;
  const disabledTooltipText = isTagRequested
    ? "Tag location cannot be edited while the tag is in Requested status"
    : "";

  async function handleSave() {
    if (!onSaveLocation || !hasChanges) {
      return;
    }

    setIsSaving(true);
    try {
      await onSaveLocation({
        tagId: tag.tagId,
        location: localLocation,
        currentStatus: tag.tagStatus,
      });
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!onDeleteTag) {
      return;
    }

    setIsDeleting(true);
    try {
      await onDeleteTag(tag.tagId);
    } finally {
      setIsDeleting(false);
    }
  }

  if (isMobile) {
    return (
      <Box
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          paddingY: 1.5,
        })}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginBottom: 1 }}
        >
          <Text variant="body2">
            Tag #{tag.tagNumber} — {formatStatus(tag.tagStatus)}
          </Text>
          <Stack direction="row" alignItems="center">
            <Tooltip title={disabledTooltipText}>
              <span>
                <Button
                  variant="text"
                  size="small"
                  disabled={isSaving || !hasChanges || isTagRequested}
                  onClick={async () => {
                    await handleSave();
                  }}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={!canDelete ? (deleteDisabledReason ?? "") : ""}>
              <span>
                <IconButton
                  size="small"
                  disabled={isDeleting || !canDelete}
                  sx={(theme) => ({ color: theme.palette.grey[500] })}
                  onClick={async () => {
                    await handleDelete();
                  }}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Tooltip title={disabledTooltipText}>
          <span>
            <TextField
              size="small"
              value={localLocation}
              placeholder={`Please enter tag location${isLocationRequired ? " (sets tag status to Set up)" : ""}`}
              disabled={isSaving || isTagRequested}
              sx={{ width: "100%" }}
              onChange={(event) => {
                setLocalLocation(event.target.value);
              }}
            />
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <TableRow>
      <TableCell>{tag.tagNumber}</TableCell>
      <TableCell>{formatStatus(tag.tagStatus)}</TableCell>
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Tooltip title={disabledTooltipText}>
            <span style={{ flexGrow: 1, minWidth: 0 }}>
              <TextField
                size="small"
                value={localLocation}
                placeholder={`Please enter tag location${isLocationRequired ? " (sets tag status to Set up)" : ""}`}
                disabled={isSaving || isTagRequested}
                sx={{ width: "100%" }}
                onChange={(event) => {
                  setLocalLocation(event.target.value);
                }}
              />
            </span>
          </Tooltip>
          <Tooltip title={disabledTooltipText}>
            <span>
              <Button
                variant="text"
                size="small"
                disabled={isSaving || !hasChanges || isTagRequested}
                onClick={async () => {
                  await handleSave();
                }}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={!canDelete ? (deleteDisabledReason ?? "") : ""}>
            <span>
              <IconButton
                size="small"
                disabled={isDeleting || !canDelete}
                sx={(theme) => ({ color: theme.palette.grey[500] })}
                onClick={async () => {
                  await handleDelete();
                }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
